import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, X, Save, Upload, Download, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { volumeService } from '../../services/volumeService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import GridPager from '../../components/GridPager';
import { FilterPopover } from '../../components/gridFilters';
import { cell, GRID } from '../../styles/gridStyles';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { makeVolumeSpec, exportVolumeTemplate, parseVolumeTemplate } from '../../utils/volumesExcel';

// =============================================================================
// VolumeGridPage
// Full-page volume editor built on the shared MantleGrid (same entry feel as
// the Opex build / pricing grids). Parameterised by `kind`:
//   * 'sales'      -> bud_sales_volumes  (SKU x month, targeting at least one of
//                     site / customer / customer group)
//   * 'production' -> bud_production_volumes (SKU x site x month)
// Version comes from the top bar. Rows are added via a small dimension picker;
// the 12 month cells are edited inline. A toolbar Import/Export dialog does the
// Excel round-trip with an append / overwrite / replace mode.
// =============================================================================

const IMPORT_MODES = [
  { id: 'overwrite', label: 'Overwrite', hint: 'Replace the quantity of any matching SKU / month.' },
  { id: 'append', label: 'Append (add)', hint: 'Add the uploaded quantity to what is already there.' },
  { id: 'replace', label: 'Replace all', hint: "Wipe this version's volumes first, then load the file." },
];

// The grid never loads a whole version. A sales plan by customer is twelve rows
// per SKU-per-customer, so 500k planning lines is six million rows — enough to
// kill the tab and the Node process on the way. The server returns this many
// matches and reports how many it held back; narrowing the filter is how you
// reach the rest.
const PAGE_LIMIT = 100;

const keyOf = (dimKeys, obj) => dimKeys.map((k) => obj[k] || '').join('|');

// Dimensions rendered as a searchable type-ahead (large datasets) rather than a
// plain <select>. Site stays a select (small, fixed list).
const SEARCHABLE_DIMS = new Set(['stock_code', 'customer_code', 'customer_group_code']);

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

// ---- Searchable picker (type-ahead) -----------------------------------------
// A lightweight combobox for large lists: type to filter by code or
// description, arrow/enter to pick. Stores the code, shows "code — desc".
// `allowClear` adds a "(None)" row so optional channels can be unset.
const SearchSelect = ({ options, value, onChange, codeKey, descKey, placeholder, allowClear = false }) => {
  const labelMap = useMemo(
    () => Object.fromEntries(options.map((o) => [o[codeKey], o[descKey]])),
    [options, codeKey, descKey],
  );
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  const display = value ? `${value}${labelMap[value] ? ' — ' + labelMap[value] : ''}` : '';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter((o) =>
          String(o[codeKey]).toLowerCase().includes(q) || String(o[descKey] || '').toLowerCase().includes(q))
      : options;
    return list.slice(0, 50);
  }, [options, query, codeKey, descKey]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const choose = (o) => { onChange(o ? o[codeKey] : ''); setQuery(''); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        className={inputClass}
        placeholder={placeholder}
        value={open ? query : display}
        onFocus={() => { setQuery(''); setHi(0); setOpen(true); }}
        onChange={(e) => { setQuery(e.target.value); setHi(0); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (matches[hi]) choose(matches[hi]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm">
          {allowClear && value && (
            <li onMouseDown={(e) => { e.preventDefault(); choose(null); }}
              className="px-3 py-1.5 cursor-pointer text-slate-400 italic hover:bg-slate-100 dark:hover:bg-slate-700">
              (None)
            </li>
          )}
          {matches.length === 0 && <li className="px-3 py-2 text-slate-400">No matches</li>}
          {matches.map((o, i) => (
            <li key={o[codeKey]}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setHi(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === hi ? 'bg-slate-100 dark:bg-slate-700' : ''}`}>
              <span className="font-medium text-slate-800 dark:text-slate-100">{o[codeKey]}</span>
              {o[descKey] && <span className="text-slate-500 dark:text-slate-400"> — {o[descKey]}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---- Add-row modal ----------------------------------------------------------
const AddRowModal = ({ spec, refData, existingKeys, defaults, onAdd, onClose }) => {
  const [dims, setDims] = useState(() =>
    Object.fromEntries(spec.dims.map((d) => [d.key, defaults?.[d.key] || ''])));

  const setDim = (k, v) => setDims((prev) => ({ ...prev, [k]: v }));

  const missingRequired = spec.dims.filter((d) => d.required && !dims[d.key]).map((d) => d.header);
  const needChannel = spec.requireChannel && !spec.channelKeys.some((k) => dims[k]);
  const dup = existingKeys.has(keyOf(spec.dims.map((d) => d.key), dims));
  const problem = missingRequired.length
    ? `${missingRequired.join(' and ')} required.`
    : needChannel ? 'Pick at least one of site, customer, or customer group.'
    : dup ? 'That combination already exists in the grid.' : '';
  const canAdd = !problem;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add row</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {spec.kind === 'sales'
                ? 'Choose a SKU and at least one channel (site, customer or group).'
                : 'Choose a site and SKU.'}
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {spec.dims.map((d) => (
            <label key={d.key} className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              {d.header}{d.required ? <span className="text-red-600 dark:text-red-400"> (required)</span> : ' (optional)'}
              {SEARCHABLE_DIMS.has(d.key) ? (
                <SearchSelect
                  options={refData[d.listKey] || []}
                  value={dims[d.key]}
                  onChange={(code) => setDim(d.key, code)}
                  codeKey={d.codeKey}
                  descKey={d.descKey}
                  allowClear={!d.required}
                  placeholder={d.key === 'stock_code' ? 'Type to search finished-good SKUs…' : `Type to search ${d.header}…`}
                />
              ) : (
                <select className={inputClass} value={dims[d.key]} onChange={(e) => setDim(d.key, e.target.value)}>
                  <option value="">{d.required ? `Select ${d.header}…` : '(None)'}</option>
                  {(refData[d.listKey] || []).map((o) => (
                    <option key={o[d.codeKey]} value={o[d.codeKey]}>
                      {o[d.codeKey]} — {o[d.descKey]}
                    </option>
                  ))}
                </select>
              )}
            </label>
          ))}

          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem}</p>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
              Cancel
            </button>
            <button type="button" onClick={() => onAdd({ ...dims })} disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}>
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Import / Export modal --------------------------------------------------
const PortModal = ({ kind, mode, setMode, isPorting, importErrors, onExport, onImport, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-6"
    onMouseDown={(e) => { if (e.target === e.currentTarget && !isPorting) onClose(); }}>
    <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Import / Export — {kind === 'production' ? 'Production' : 'Sales'} Volumes</h3>
        <button type="button" onClick={onClose} disabled={isPorting}
          className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5">
        <h4 className="text-xs font-semibold text-slate-900 dark:text-slate-100 mb-2 uppercase tracking-wide">On import, existing volumes should be…</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {IMPORT_MODES.map((m) => (
            <label key={m.id}
              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                mode === m.id ? 'border-slate-900 bg-slate-50 dark:bg-slate-700' : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
              }`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="portMode" className="accent-slate-900" checked={mode === m.id} onChange={() => setMode(m.id)} />
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{m.label}</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{m.hint}</p>
            </label>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Export Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Excel pre-filled with the current grid. Dimension columns carry dropdowns; enter quantities under P1–P12.
            </p>
            <button onClick={onExport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 disabled:opacity-50">
              <Download className="w-4 h-4" /> {isPorting ? 'Working…' : 'Export'}
            </button>
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 flex flex-col">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Template</h4>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex-1">
              Upload a completed template. Every row is validated; if anything is off, nothing is saved.
            </p>
            <button onClick={onImport} disabled={isPorting}
              className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {isPorting ? 'Working…' : `Import (${IMPORT_MODES.find((m) => m.id === mode).label})`}
            </button>
          </div>
        </div>

        {importErrors.length > 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-700 mb-1.5">{importErrors.length} row(s) need attention — nothing was imported:</p>
            <ul className="text-xs text-red-600 list-disc pl-5 space-y-0.5 max-h-40 overflow-auto">
              {importErrors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ---- Page -------------------------------------------------------------------
const VolumeGridPage = ({ kind = 'sales' }) => {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();
  const spec = useMemo(() => makeVolumeSpec(kind), [kind]);
  const dimKeys = useMemo(() => spec.dims.map((d) => d.key), [spec]);

  const [refData, setRefData] = useState({ customers: [], customerGroups: [], sites: [], stockCodes: [] });
  const [rows, setRows] = useState([]);   // working rows: { key, dims, periods{p:num}, lineIds{p:uuid}, uom, isNew }
  const [dirty, setDirty] = useState({}); // { `${key}:${p}`: true }
  // Server-side filtering and paging: a version can hold millions of rows, so
  // the grid shows one page and the server reports how much matched.
  const [filters, setFilters] = useState({});   // { colId: { text, values } }
  const [pageInfo, setPageInfo] = useState({ total: null, hasMore: false });
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [filterUI, setFilterUI] = useState(null); // { colId, options, x, y }

  const activeFilterIds = useMemo(() => Object.keys(filters), [filters]);

  // Changing a filter or the page size has to reset to the first page —
  // otherwise a filter that matches 20 rows, applied while on page 3, shows
  // nothing and looks broken.
  const setFilter = useCallback((colId, f) => {
    setOffset(0);
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [portOpen, setPortOpen] = useState(false);
  const [portMode, setPortMode] = useState('overwrite');
  const [isPorting, setIsPorting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);

  // ---- Load reference data once -------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const r = await volumeService.getReferenceData();
        if (r.success) setRefData(r.data);
      } catch { /* dropdowns will be empty */ }
    })();
  }, []);

  const labelMaps = useMemo(() => {
    const m = {};
    spec.dims.forEach((d) => {
      m[d.key] = Object.fromEntries((refData[d.listKey] || []).map((o) => [o[d.codeKey], o[d.descKey]]));
    });
    return m;
  }, [refData, spec]);

  // ---- Load grid rows ------------------------------------------------------
  const toWorkingRow = useCallback((r) => {
    const periods = {};
    const lineIds = {};
    Object.entries(r.period_data || {}).forEach(([p, c]) => {
      periods[Number(p)] = Number(c.quantity) || 0;
      if (c.line_id) lineIds[Number(p)] = c.line_id;
    });
    const dims = Object.fromEntries(dimKeys.map((k) => [k, r[k] || null]));
    return { key: keyOf(dimKeys, dims), dims, periods, lineIds, uom: r.unit_of_measure || null, isNew: false };
  }, [dimKeys]);

  const loadRows = useCallback(async () => {
    if (!selectedVersion) { setRows([]); setPageInfo({ total: null, hasMore: false }); return; }
    setIsLoading(true);
    setError(null);
    try {
      const opts = { filters, limit, offset };
      const res = kind === 'production'
        ? await volumeService.getProductionVolumes(selectedVersion, opts)
        : await volumeService.getSalesVolumes(selectedVersion, opts);
      // The endpoints now return { rows, limit, hasMore, total } rather than a
      // bare array: a full version can be millions of rows, so the server sends
      // a capped slice and says how much it held back.
      const payload = res.success ? (res.data || {}) : {};
      setRows((payload.rows || []).map(toWorkingRow));
      setPageInfo({ total: payload.total ?? null, hasMore: !!payload.hasMore });
      setDirty({});
    } catch (e) {
      setError('Failed to load volumes.');
      setRows([]);
      setPageInfo({ total: null, hasMore: false });
    } finally {
      setIsLoading(false);
    }
  }, [selectedVersion, kind, toWorkingRow, filters, limit, offset]);

  // Debounced: the filter box would otherwise fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(loadRows, 250);
    return () => clearTimeout(t);
  }, [loadRows]);

  // ---- Header filter menus -------------------------------------------------
  // Options come from the master lists (refData), NOT from the loaded page —
  // the page holds at most `limit` rows, so building the checklist from it
  // would only ever offer values you can already see. The master lists are
  // small and are already in memory for the add-row picker.
  const onHeaderMenu = useCallback((columnId, bounds) => {
    const dim = spec.dims.find((d) => d.key === columnId);
    if (!dim) return;   // month / total / delete columns have no filter
    const options = [...new Set((refData[dim.listKey] || []).map((o) => String(o[dim.codeKey] ?? '')))].sort();
    setFilterUI({
      colId: columnId,
      options,
      x: bounds ? bounds.x : 200,
      y: bounds ? bounds.y + bounds.height : 200,
    });
  }, [spec, refData]);

  // ---- Grid model ----------------------------------------------------------
  const qtyOf = (r, p) => Number(r.periods?.[p] || 0);
  const rowTotal = (r) => PERIODS.reduce((a, p) => a + qtyOf(r, p), 0);

  const columns = useMemo(() => ([
    // hasMenu puts a ▾ on the dimension headings. The legacy MantleGrid adapter
    // passes it straight through to Glide and reports the click as
    // onHeaderMenuClick(columnId, bounds) — so header filters work here without
    // porting the page off the adapter.
    ...spec.dims.map((d, i) => ({
      columnId: d.key, width: i === 0 ? 210 : 150, resizable: true, hasMenu: true,
    })),
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 76, resizable: true })),
    { columnId: 'total', width: 104, resizable: true },
    { columnId: 'del', width: 40, resizable: false },
  ]), [spec]);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        ...spec.dims.map((d) => cell.header(d.header)),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header('Total', { align: 'right' }),
        cell.header(''),
      ],
    };

    const dataRows = rows.map((r, idx) => ({
      rowId: r.key,
      height: GRID.size.dataRow,
      cells: [
        ...spec.dims.map((d, di) => {
          const code = r.dims[d.key];
          if (!code) return cell.text('—', { idx });
          const label = labelMaps[d.key]?.[code];
          const shown = label ? `${code} · ${label}` : code;
          return di === 0 ? cell.label(shown, { idx }) : cell.text(shown, { idx });
        }),
        ...PERIODS.map((p) => cell.number(qtyOf(r, p), { editable: true, idx })),
        cell.aggregate(rowTotal(r), { kind: 'year' }),
        cell.text('✕', { idx, color: GRID.color.muted }),
      ],
    }));

    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('TOTAL'),
        ...spec.dims.slice(1).map(() => cell.totalBlank()),
        ...PERIODS.map((p) => cell.totalNumber(rows.reduce((a, r) => a + qtyOf(r, p), 0))),
        cell.totalNumber(rows.reduce((a, r) => a + rowTotal(r), 0)),
        cell.totalBlank(),
      ],
    };

    return [headerRow, ...dataRows, totalRow];
  }, [rows, spec, labelMaps, fyStartMonth]);

  // ---- Edits ---------------------------------------------------------------
  const handleChanges = (changes) => {
    setRows((prev) => {
      const next = prev.map((r) => ({ ...r, periods: { ...r.periods } }));
      // A multi-cell paste arrives as one onCellsChanged call PER CELL in a
      // single tick. Collect this call's keys locally and merge them with a
      // functional setDirty — seeding from the `dirty` closure instead would
      // restart from the same stale base each call and only the last pasted
      // cell would survive to be saved.
      const touched = {};
      for (const ch of changes) {
        const col = String(ch.columnId);
        if (!col.startsWith('P')) continue;
        const period = Number(col.slice(1));
        if (!(period >= 1 && period <= 12)) continue;
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        const row = next.find((r) => r.key === ch.rowId);
        if (!row) continue;
        row.periods[period] = val;
        touched[`${row.key}:${period}`] = true;
      }
      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  // Clicking the trailing ✕ deletes the row (with confirm).
  const handleFocus = (loc) => {
    if (String(loc.columnId) !== 'del') return true;
    const rowId = String(loc.rowId);
    if (rowId === 'header' || rowId === 'totals') return true;
    const row = rows.find((r) => r.key === rowId);
    if (row) deleteRow(row);
    return false;
  };

  // ---- Add row -------------------------------------------------------------
  const existingKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);

  const confirmAdd = (dims) => {
    const key = keyOf(dimKeys, dims);
    if (existingKeys.has(key)) return;
    setRows((prev) => [
      { key, dims: Object.fromEntries(dimKeys.map((k) => [k, dims[k] || null])), periods: {}, lineIds: {}, uom: null, isNew: true },
      ...prev,
    ]);
    setAddOpen(false);
  };

  // ---- Delete row ----------------------------------------------------------
  const deleteRow = async (row) => {
    const label = dimKeys.map((k) => row.dims[k]).filter(Boolean).join(' / ');
    if (row.isNew) { // never saved — just drop it locally
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      return;
    }
    if (!window.confirm(`Delete all months for ${label}?`)) return;
    setError(null);
    try {
      if (kind === 'production') {
        await volumeService.deleteProductionVolumes(selectedVersion, [{ site_code: row.dims.site_code, stock_code: row.dims.stock_code }]);
      } else {
        const ids = Object.values(row.lineIds || {});
        if (ids.length) await volumeService.deleteSalesVolumes(ids);
      }
      await loadRows();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete row.');
    }
  };

  // ---- Save ----------------------------------------------------------------
  const handleSave = async () => {
    setError(null);
    const entries = [];
    for (const r of rows) {
      for (const p of PERIODS) {
        if (!dirty[`${r.key}:${p}`]) continue;
        entries.push({ ...r.dims, financial_period: p, quantity: qtyOf(r, p), unit_of_measure: r.uom || null });
      }
    }
    if (entries.length === 0) return;

    setIsSaving(true);
    try {
      const payload = { version_id: selectedVersion, mode: 'overwrite', rows: entries };
      const res = kind === 'production'
        ? await volumeService.upsertProductionVolumes(payload)
        : await volumeService.upsertSalesVolumes(payload);
      if (!res.success) throw new Error(res.message);
      await loadRows();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save volumes.');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Import / Export -----------------------------------------------------
  // Reconstruct view-shaped rows (period_data) from the current grid so an
  // export reflects on-screen values.
  const viewRowsForExport = useMemo(() => rows.map((r) => ({
    ...r.dims,
    unit_of_measure: r.uom,
    period_data: Object.fromEntries(PERIODS.filter((p) => r.periods[p] != null).map((p) => [String(p), { quantity: r.periods[p] }])),
  })), [rows]);

  // Export re-queries rather than dumping what is on screen. The grid holds at
  // most PAGE_LIMIT rows, so exporting `rows` would hand someone a 100-line
  // file that looks complete — and if they edited it and re-imported with
  // "Replace all", the rest of the version would be gone. It honours the
  // current filters (export what you are looking at) but lifts the cap.
  const handleExport = async () => {
    setImportErrors([]); setIsPorting(true);
    try {
      const opts = { filters, limit: 50000, scope: 'export' };
      const res = kind === 'production'
        ? await volumeService.getProductionVolumes(selectedVersion, opts)
        : await volumeService.getSalesVolumes(selectedVersion, opts);
      const payload = res.success ? (res.data || {}) : {};
      const full = payload.rows || [];
      if (payload.hasMore) {
        setError('More than 50,000 rows match — the export was capped. Narrow the filter and export in parts.');
      }
      await exportVolumeTemplate({
        kind,
        versionLabel: selectedVersion,
        ref: refData,
        // Fall back to the on-screen rows only if the re-query returned nothing
        // at all, so an export is never silently empty.
        rows: full.length ? full : viewRowsForExport,
      });
    } catch { setError('Failed to generate the Excel template.'); }
    finally { setIsPorting(false); }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportErrors([]); setError(null); setIsPorting(true);
    try {
      const { rows: parsed, errors } = await parseVolumeTemplate(file, kind, refData);
      if (errors.length) { setImportErrors(errors); return; }
      if (parsed.length === 0 && portMode !== 'replace') { setError('No data rows found in the template.'); return; }
      const payload = { version_id: selectedVersion, mode: portMode, rows: parsed };
      const res = kind === 'production'
        ? await volumeService.upsertProductionVolumes(payload)
        : await volumeService.upsertSalesVolumes(payload);
      if (!res.success) throw new Error(res.message);
      setPortOpen(false);
      await loadRows();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to read or import the template.');
    } finally { setIsPorting(false); }
  };

  const hasChanges = Object.keys(dirty).length > 0;
  const title = kind === 'production' ? 'Production Volumes' : 'Sales / Sell-out Volumes';
  const subtitle = kind === 'production'
    ? 'Optional production volumes by SKU and site, per month. Version comes from the top bar.'
    : 'Sell-out / customer sales volumes by SKU and month — each row targets a site, a customer, or a customer group. Version comes from the top bar.';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!selectedVersion ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select a budget version in the top bar to begin.
            </div>
          ) : (
            <>
            {/* flex-1 min-h-0 so the grid takes the space the pager leaves —
                MantleGrid sizes to 100% of its parent, and without a bounded
                flex child that resolves against nothing. */}
            <div className="flex-1 min-h-0">
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onFocusLocationChanging={handleFocus}
              onHeaderMenuClick={onHeaderMenu}
              hint="Enter each month's volume. ▾ on a dimension heading filters — the filter runs on the server, so it searches the whole version, not just this page. ✕ removes a row."
              toolbar={
                <>
                  {activeFilterIds.length > 0 && (
                    <MantleGridButton onClick={() => { setFilters({}); setOffset(0); }}>
                      Clear filters ({activeFilterIds.length})
                    </MantleGridButton>
                  )}
                  <MantleGridButton onClick={() => setAddOpen(true)}>+ Add row</MantleGridButton>
                  <MantleGridButton onClick={() => { setImportErrors([]); setPortOpen(true); }}>Import / Export</MantleGridButton>
                  <MantleGridButton onClick={handleSave} disabled={isSaving || !hasChanges} accent={hasChanges}>
                    {isSaving ? 'Saving…' : 'Save'}
                  </MantleGridButton>
                </>
              }
            />
            </div>
            <GridPager
              offset={offset}
              limit={limit}
              rowCount={rows.length}
              total={pageInfo.total}
              hasMore={pageInfo.hasMore}
              loading={isLoading}
              onOffset={setOffset}
              onLimit={(n) => { setLimit(n); setOffset(0); }}
            />
            </>
          )}
        </div>
      </div>

      {/* Column filter popover — same component the MantleDataGrid pages use.
          Applying one resets to page 1 and refetches from the server. */}
      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={filterUI}
          options={filterUI.options}
          filter={filters[filterUI.colId]}
          onApply={(f) => setFilter(filterUI.colId, f)}
          onClose={() => setFilterUI(null)}
        />
      )}

      {addOpen && (
        <AddRowModal
          spec={spec}
          refData={refData}
          existingKeys={existingKeys}
          defaults={kind === 'production' ? { site_code: selectedSite } : { site_code: selectedSite }}
          onAdd={confirmAdd}
          onClose={() => setAddOpen(false)}
        />
      )}

      {portOpen && (
        <>
          <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileSelected} />
          <PortModal
            kind={kind}
            mode={portMode}
            setMode={setPortMode}
            isPorting={isPorting}
            importErrors={importErrors}
            onExport={handleExport}
            onImport={() => fileInputRef.current?.click()}
            onClose={() => { if (!isPorting) setPortOpen(false); }}
          />
        </>
      )}
    </div>
  );
};

export default VolumeGridPage;
